import React, { useState, useEffect } from "react";
import { X, Users, Mail, Shield, Eye, Edit, Trash2, UserPlus, Clock, CheckCircle, XCircle, Copy, Check } from "lucide-react";
import {
  getSchoolUsers,
  getSchoolInvitations,
  createUserInvitation,
  updateUserRole,
  removeUserFromSchool,
  cancelInvitation,
  resendInvitation,
  SCHOOL_USER_ROLES,
  canManageUsers,
} from "../../services/userManagementService";
import "../../styles/UserManagementModal.css";

const UserManagementModal = ({ isOpen, onClose, school, currentUser, userRole, schoolRole }) => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState(SCHOOL_USER_ROLES.SCHOOL_VIEWER);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [copiedLink, setCopiedLink] = useState(false);
  const [invitationLink, setInvitationLink] = useState("");

  const canManage = canManageUsers(userRole, schoolRole);

  useEffect(() => {
    if (isOpen && school) {
      loadUsersAndInvitations();
    }
  }, [isOpen, school]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
        setInvitationLink("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Auto-dismiss error messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadUsersAndInvitations = async () => {
    setLoading(true);
    try {
      const [schoolUsers, schoolInvitations] = await Promise.all([
        getSchoolUsers(school.id),
        getSchoolInvitations(school.id),
      ]);
      setUsers(schoolUsers);
      setInvitations(schoolInvitations);
    } catch (error) {
      console.error("Error loading users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const invitation = await createUserInvitation(
        school.id,
        inviteEmail,
        inviteRole,
        currentUser.uid
      );
      
      // Show success message without raw URL
      setSuccess(`Invitation sent successfully to ${inviteEmail}`);
      setInvitationLink(invitation.inviteUrl);
      setInviteEmail("");
      setInviteRole(SCHOOL_USER_ROLES.SCHOOL_VIEWER);
      setShowInviteForm(false);
      
      // Reload invitations
      await loadUsersAndInvitations();
    } catch (error) {
      setError(error.message || "Failed to send invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    setLoading(true);
    try {
      await updateUserRole(userId, newRole);
      setSuccess("User role updated successfully");
      await loadUsersAndInvitations();
    } catch (error) {
      setError(error.message || "Failed to update user role");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from this school?`)) {
      return;
    }

    setLoading(true);
    try {
      await removeUserFromSchool(userId, school.id);
      setSuccess("User removed successfully");
      await loadUsersAndInvitations();
    } catch (error) {
      setError(error.message || "Failed to remove user");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId, email) => {
    if (!window.confirm(`Cancel invitation to ${email}?`)) {
      return;
    }

    setLoading(true);
    try {
      await cancelInvitation(invitationId);
      setSuccess("Invitation cancelled");
      await loadUsersAndInvitations();
    } catch (error) {
      setError(error.message || "Failed to cancel invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    setLoading(true);
    try {
      const result = await resendInvitation(invitationId);
      setSuccess(`Invitation resent to ${result.email}`);
    } catch (error) {
      setError(error.message || "Failed to resend invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(invitationLink);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const getUserInitials = (user) => {
    const name = user.displayName || user.email || 'Unknown';
    const parts = name.split(/[@\s]/);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getInitialColor = (str) => {
    if (!str || typeof str !== 'string') {
      return '#64748b'; // Default gray if string is invalid
    }
    // Professional color palette
    const colors = [
      '#3b82f6', // blue
      '#10b981', // emerald
      '#8b5cf6', // violet
      '#f59e0b', // amber
      '#ef4444', // red
      '#14b8a6', // teal
      '#f97316', // orange
      '#6366f1', // indigo
      '#84cc16', // lime
      '#06b6d4', // cyan
    ];
    
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case SCHOOL_USER_ROLES.SCHOOL_ADMIN:
        return <Shield className="role-icon admin" />;
      case SCHOOL_USER_ROLES.SCHOOL_EDITOR:
        return <Edit className="role-icon editor" />;
      case SCHOOL_USER_ROLES.SCHOOL_VIEWER:
        return <Eye className="role-icon viewer" />;
      default:
        return <Users className="role-icon" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case SCHOOL_USER_ROLES.SCHOOL_ADMIN:
        return "Admin";
      case SCHOOL_USER_ROLES.SCHOOL_EDITOR:
        return "Editor";
      case SCHOOL_USER_ROLES.SCHOOL_VIEWER:
        return "Viewer";
      default:
        return role;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="user-management-modal-overlay" onClick={onClose}>
      <div className="user-management-modal-content user-management-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-management-modal-header">
          <div className="user-management-modal-title-section">
            <Users className="user-management-modal-icon" />
            <h2 className="user-management-modal-title">Manage Users - {school?.name}</h2>
          </div>
          <button onClick={onClose} className="user-management-modal-close">
            <X />
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <XCircle className="alert-icon" />
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <CheckCircle className="alert-icon" />
            <div className="alert-content">
              <span>{success}</span>
              {invitationLink && (
                <button
                  onClick={handleCopyLink}
                  className="copy-link-button"
                  title="Copy invitation link"
                >
                  {copiedLink ? (
                    <>
                      <Check style={{ width: "14px", height: "14px" }} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy style={{ width: "14px", height: "14px" }} />
                      Copy Link
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="user-management-modal-body">
          {canManage && (
            <div className="action-bar">
              {!showInviteForm ? (
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="button button-primary"
                  disabled={loading}
                >
                  <UserPlus style={{ width: "16px", height: "16px" }} />
                  Invite User
                </button>
              ) : (
                <form onSubmit={handleInviteUser} className="invite-form">
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="input"
                    required
                    disabled={loading}
                  />
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    disabled={loading}
                  >
                    <option value={SCHOOL_USER_ROLES.SCHOOL_VIEWER}>Viewer</option>
                    <option value={SCHOOL_USER_ROLES.SCHOOL_EDITOR}>Editor</option>
                    <option value={SCHOOL_USER_ROLES.SCHOOL_ADMIN}>Admin</option>
                  </select>
                  <div className="invite-form-buttons">
                    <button type="submit" className="button button-primary" disabled={loading}>
                      <Mail style={{ width: "14px", height: "14px" }} />
                      Send Invite
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteForm(false);
                        setInviteEmail("");
                        setError("");
                      }}
                      className="button button-outline"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          <div className="users-section">
            <h3 className="section-title">Active Users ({users.length})</h3>
            {loading && <div className="loading">Loading users...</div>}
            {!loading && users.length === 0 && (
              <div className="empty-state">
                <Users className="empty-icon" />
                <p>No users found for this school</p>
              </div>
            )}
            {!loading && users.length > 0 && (
              <div className="users-list">
                {users.map((user) => (
                  <div key={user.id} className="user-management-modal-user-item">
                    <div className="user-management-modal-user-avatar" style={{ backgroundColor: getInitialColor(user.email || user.id) }}>
                      {getUserInitials(user)}
                    </div>
                    <div className="user-management-modal-user-info">
                      <div className="user-management-modal-user-name">
                        <span>
                          {user.displayName || (user.email ? user.email.split('@')[0] : 'Unknown')}
                        </span>
                        {user.id === currentUser?.uid && (
                          <span className="current-user-badge">You</span>
                        )}
                      </div>
                      <div className="user-management-modal-user-email">{user.email || 'No email provided'}</div>
                    </div>
                    <div className="user-management-modal-user-actions">
                      <div className="user-management-modal-user-role">
                        {getRoleIcon(user.schoolRole)}
                        {canManage && user.id !== currentUser?.uid ? (
                          <select
                            value={user.schoolRole || SCHOOL_USER_ROLES.SCHOOL_VIEWER}
                            onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                            className="role-select"
                            disabled={loading}
                          >
                            <option value={SCHOOL_USER_ROLES.SCHOOL_VIEWER}>Viewer</option>
                            <option value={SCHOOL_USER_ROLES.SCHOOL_EDITOR}>Editor</option>
                            <option value={SCHOOL_USER_ROLES.SCHOOL_ADMIN}>Admin</option>
                          </select>
                        ) : (
                          <span className="role-label">
                            {getRoleLabel(user.schoolRole || SCHOOL_USER_ROLES.SCHOOL_VIEWER)}
                          </span>
                        )}
                      </div>
                      {canManage && user.id !== currentUser?.uid && (
                        <button
                          onClick={() => handleRemoveUser(user.id, user.displayName || user.email)}
                          className="button-icon button-danger"
                          title="Remove user"
                          disabled={loading}
                        >
                          <Trash2 style={{ width: "16px", height: "16px" }} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {invitations.length > 0 && (
            <div className="invitations-section">
              <h3 className="section-title">Pending Invitations ({invitations.length})</h3>
              <div className="invitations-list">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="user-management-modal-invitation-item">
                    <div className="user-management-modal-invitation-info">
                      <div className="user-management-modal-invitation-email">{invitation.email}</div>
                      <div className="invitation-meta">
                        <Clock className="meta-icon" />
                        Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="user-management-modal-invitation-actions">
                      <span className="role-label">
                        {getRoleIcon(invitation.role)}
                        {getRoleLabel(invitation.role)}
                      </span>
                      {canManage && (
                        <>
                          <button
                            onClick={() => handleResendInvitation(invitation.id)}
                            className="button-text"
                            disabled={loading}
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                            className="button-text button-danger"
                            disabled={loading}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementModal;